import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { HotelProvider } from "@/context/HotelContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { useState, useEffect } from "react";
import axios from "axios";

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallback from "@/pages/AuthCallback";
import FirstSetupPage from "@/pages/FirstSetupPage";
import DashboardPage from "@/pages/DashboardPage";
import RoomsPage from "@/pages/RoomsPage";
import ReservationsPage from "@/pages/ReservationsPage";
import GuestsPage from "@/pages/GuestsPage";
import BillingPage from "@/pages/BillingPage";
import HousekeepingPage from "@/pages/HousekeepingPage";
import MaintenancePage from "@/pages/MaintenancePage";
import TransfersPage from "@/pages/TransfersPage";
import ReportsPage from "@/pages/ReportsPage";
import ExpensesPage from "@/pages/ExpensesPage";
import ChannelManagerPage from "@/pages/ChannelManagerPage";
import StaffPage from "@/pages/StaffPage";
import SettingsPage from "@/pages/SettingsPage";
import HotelSetupPage from "@/pages/HotelSetupPage";
import CalendarPage from "@/pages/CalendarPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import PlatformAdminPage from "@/pages/PlatformAdminPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import ProfilePage from "@/pages/ProfilePage";
import { API } from "@/config/api";

// Layout
import MainLayout from "@/components/layout/MainLayout";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const HotelRequired = ({ children }) => {
  const { user } = useAuth();
  
  if (user && !user.hotel_id) {
    return <Navigate to="/setup" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [setupRequired, setSetupRequired] = useState(null); // null = checking

  useEffect(() => {
    // Only check setup status if not already authenticated
    if (!loading && !user) {
      axios.get(`${API}/auth/setup-status`)
        .then(res => setSetupRequired(res.data.setup_required))
        .catch(() => setSetupRequired(false));
    } else if (!loading) {
      setSetupRequired(false);
    }
  }, [loading, user]);

  // Show loading while checking auth + setup state
  if (loading || setupRequired === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Check for session_id in hash - process auth callback synchronously before render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // First-time deployment: no users in DB → show setup page
  // BUT allow password reset to work even during setup
  if (setupRequired && !user) {
    // Allow reset-password and forgot-password routes even during setup
    if (location.pathname === '/reset-password' || location.pathname === '/forgot-password') {
      return (
        <Routes>
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="*" element={<FirstSetupPage />} />
        </Routes>
      );
    }
    return (
      <Routes>
        <Route path="*" element={<FirstSetupPage />} />
      </Routes>
    );
  }
  
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/" replace /> : <RegisterPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/" replace /> : <ForgotPasswordPage />} />
      <Route path="/reset-password" element={user ? <Navigate to="/" replace /> : <ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      <Route path="/setup" element={
        <ProtectedRoute>
          <HotelSetupPage />
        </ProtectedRoute>
      } />
      
      <Route path="/platform-admin" element={
        <ProtectedRoute>
          <PlatformAdminPage />
        </ProtectedRoute>
      } />
      
      <Route path="/super-admin" element={
        <ProtectedRoute>
          <SuperAdminPage />
        </ProtectedRoute>
      } />
      
      <Route path="/" element={
        <ProtectedRoute>
          <HotelRequired>
            <HotelProvider>
              <MainLayout />
            </HotelProvider>
          </HotelRequired>
        </ProtectedRoute>
      }>
        <Route index element={<DashboardPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="rooms" element={<RoomsPage />} />
        <Route path="reservations" element={<ReservationsPage />} />
        <Route path="guests" element={<GuestsPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="housekeeping" element={<HousekeepingPage />} />
        <Route path="maintenance" element={<MaintenancePage />} />
        <Route path="transfers" element={<TransfersPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="channels" element={<ChannelManagerPage />} />
        <Route path="staff" element={<StaffPage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
